import React from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { 
  DollarSign, 
  MessageCircle, 
  Copy, 
  Link2, 
  AlertTriangle 
} from 'lucide-react';
import { toast } from 'sonner';

interface PaymentLinkShareModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  paymentLink: string | null;
  amount: number;
  contactName?: string;
  contactPhone?: string;
}

export function PaymentLinkShareModal({
  open,
  onOpenChange,
  paymentLink,
  amount,
  contactName,
  contactPhone
}: PaymentLinkShareModalProps) {

  const formattedAmount = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(amount);

  const handleSendWhatsApp = () => {
    if (!paymentLink) return;
    
    const firstName = contactName?.split(' ')[0] || 'Cliente';
    const message = `Olá ${firstName}! 👋

Segue o link para pagamento de ${formattedAmount}:

💳 ${paymentLink}

Pagamento processado com segurança pelo Stripe.`;

    const phoneNumber = contactPhone?.replace(/\D/g, '') || '';
    const whatsappUrl = phoneNumber 
      ? `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`
      : `https://wa.me/?text=${encodeURIComponent(message)}`;
    
    window.open(whatsappUrl, '_blank');
    onOpenChange(false);
    toast.success('WhatsApp aberto com a mensagem');
  };

  const handleCopyLink = async () => {
    if (!paymentLink) return;
    
    try {
      await navigator.clipboard.writeText(paymentLink);
      toast.success('Link copiado para a área de transferência');
    } catch {
      toast.error('Erro ao copiar link');
    }
  };

  const handleOpenLink = () => {
    if (!paymentLink) return;
    window.open(paymentLink, '_blank');
    onOpenChange(false);
    toast.info('Link aberto - lembre-se do aviso de segurança');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <DollarSign className="w-5 h-5" />
            Enviar Link de Cobrança
          </DialogTitle>
          <DialogDescription>
            Valor: <span className="font-semibold text-foreground">{formattedAmount}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-4">
          {/* WhatsApp Button */}
          <Button
            variant="outline"
            className="w-full justify-start h-12 text-left"
            onClick={handleSendWhatsApp}
          >
            <MessageCircle className="w-5 h-5 mr-3 text-green-500" />
            <div>
              <div className="font-medium">Enviar por WhatsApp</div>
              <div className="text-xs text-muted-foreground">Recomendado - mais seguro</div>
            </div>
          </Button>

          {/* Copy Link Button */}
          <Button
            variant="outline"
            className="w-full justify-start h-12 text-left"
            onClick={handleCopyLink}
          >
            <Copy className="w-5 h-5 mr-3 text-blue-500" />
            <div>
              <div className="font-medium">Copiar Link</div>
              <div className="text-xs text-muted-foreground">Cole onde preferir</div>
            </div>
          </Button>

          {/* Open Link Button (with warning) */}
          <div className="pt-2 border-t">
            <Button
              variant="ghost"
              className="w-full justify-start h-12 text-left text-muted-foreground hover:text-foreground"
              onClick={handleOpenLink}
            >
              <Link2 className="w-5 h-5 mr-3" />
              <div>
                <div className="font-medium">Abrir Link (cliente presente)</div>
                <div className="text-xs">Apenas se o cliente estiver ao seu lado</div>
              </div>
            </Button>
            
            <div className="flex items-start gap-2 mt-2 p-3 bg-amber-500/10 rounded-lg text-xs text-amber-600 dark:text-amber-400">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>
                Abrir o link no seu dispositivo pode causar bloqueio por fraude no Stripe, 
                pois o IP registrado será o seu, não do cliente.
              </span>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
